import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  HttpLink,
  split,
  Operation,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { ErrorResponse, onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import DebounceLink from 'apollo-link-debounce';
import { createClient } from 'graphql-ws';
import unfetch from 'unfetch';
import { loggerLink } from '~/utils/apollo-logger-link';
import { API_URL, WEBSOCKET_URL, IS_PRODUCTION } from '~/utils/constants';
import { store } from '~/utils/store';
import { trackError } from './utils/tracking';
// Fetch polyfill

function opShowingFetch(uri, options) {
  const { operationName } = JSON.parse(options.body);

  return unfetch(`${uri}?op=${operationName}`, options);
}

const isSubscription = ({ query }: Operation) => {
  const definition = getMainDefinition(query);
  return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
};

const parseApolloErrors = (errorResponse: ErrorResponse) => {
  const { graphQLErrors, networkError } = errorResponse;
  const messages: string[] = [];
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, path }) => {
      messages.push(`[GraphQL error]: Message: ${message}, Path: ${path}`);
    });
  }
  if (networkError) {
    // temporary; to help debug ProgressEvent errors on mobile browsers
    const err = networkError.message ?? JSON.stringify(networkError);
    messages.push(`[Network error]: ${err}`);
  }
  return messages;
};

const httpLink = new HttpLink({
  credentials: 'include',
  headers: {
    Accept: 'application/json',
  },
  uri: `${API_URL}/graphql`,
  // @ts-expect-error - doesn't look like apollo typed this correctly...
  fetch: IS_PRODUCTION ? unfetch : opShowingFetch,
});

const debounceLink = new DebounceLink(100);

const retriable = [
  'failed to fetch',
  'network connection was lost',
  'request timed out',
  'socket hang up',
];

const retryLink = new RetryLink({
  attempts: {
    max: 3,
    retryIf: error => !!retriable.find(str => str.includes(error?.message?.toLowerCase())),
  },
  delay: {
    initial: 500,
  },
});

const authLink = setContext(() => {
  const token = store.get('TOKEN');
  const authorization = token ? `Bearer ${token}` : '';
  return { headers: { authorization } };
});

const refreshTokenLink = new ApolloLink((operation, forward) => {
  if (isSubscription(operation)) return forward(operation);

  return forward(operation).map(response => {
    const token = operation.getContext()?.response?.headers?.get('x-auth-token');
    if (token) store.set('TOKEN', token);
    return response;
  });
});

const wsLink = new GraphQLWsLink(
  createClient({
    connectionParams: () => ({
      authentication: `Bearer ${store.get('TOKEN')}`,
    }),
    url: `${WEBSOCKET_URL}/graphql`,
  }),
);

const splitLink = split(isSubscription, wsLink, httpLink);

const normalizeError = (message: string) => {
  // this error always comes w/ a unique URL in the message, creating a new Sentry issue each time
  if (message?.includes('socket hang up')) return 'Socket hang up';
  return message;
};

const errorLink = onError((errorResponse: ErrorResponse) => {
  const { operation } = errorResponse;
  parseApolloErrors(errorResponse).forEach(message => {
    trackError(new Error(normalizeError(message)), { extras: { operation } });
  });
});

const linkArray = [
  debounceLink,
  loggerLink,
  retryLink,
  errorLink,
  refreshTokenLink,
  authLink,
  splitLink,
];

export const apolloClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: ApolloLink.from(linkArray),
  name: 'FlexFunnel',
});
